import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { AlertTriangle, Globe, History, KeyRound, Pencil, Send, Trash2 } from 'lucide-react'
import {
  formatWebhookEventLabel,
  getVerticalAppointmentWebhookCopy,
  type WebhookEndpoint,
} from '@/services/orderWebhooksApi'
import { useVerticalUI } from '@/lib/verticalUI'

interface WebhookEndpointCardProps {
  endpoint: WebhookEndpoint
  testing: boolean
  updating: boolean
  onTest: () => void
  onHistory: () => void
  onRotate: () => void
  onDelete: () => void
  onEdit: () => void
  onToggle: (enabled: boolean) => void
}

function statusOf(endpoint: WebhookEndpoint): {
  label: string
  className: string
  hint: string | null
} {
  if (endpoint.enabled && endpoint.consecutiveFailures === 0) {
    return { label: 'Activo', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: null }
  }
  if (endpoint.enabled) {
    return {
      label: 'Con fallos',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      hint: `${endpoint.consecutiveFailures} envío(s) fallido(s) seguido(s). Revisa la dirección o el sistema de destino.`,
    }
  }
  return {
    label: 'Desactivado',
    className: 'bg-gray-100 text-gray-600 border-gray-200',
    hint: endpoint.disabledReason ?? 'Desactivado por el equipo.',
  }
}

function formatDate(value: string | null): string {
  if (!value) return 'Todavía no se ha enviado nada'
  return new Date(value).toLocaleString('es-VE', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function WebhookEndpointCard({
  endpoint,
  testing,
  updating,
  onTest,
  onHistory,
  onRotate,
  onDelete,
  onEdit,
  onToggle,
}: WebhookEndpointCardProps) {
  const { slug } = useVerticalUI()
  const { singularLabel } = getVerticalAppointmentWebhookCopy(slug)
  const hasAppointmentEvents = endpoint.events.some((event) => event.startsWith('appointment.'))
  const status = statusOf(endpoint)

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Globe className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <h3 className="break-all text-sm font-semibold text-gray-900">
              {endpoint.description || endpoint.url}
            </h3>
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
              {status.label}
            </span>
          </div>
          {endpoint.description && (
            <p className="mt-1 break-all text-xs text-gray-500">{endpoint.url}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Último aviso: {formatDate(endpoint.lastDeliveryAt)}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {endpoint.events.map((event) => (
              <span
                key={event}
                className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
              >
                {formatWebhookEventLabel(event, singularLabel)}
              </span>
            ))}
            {hasAppointmentEvents && endpoint.includeAppointmentNotes && (
              <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                Incluye motivo
              </span>
            )}
          </div>
        </div>

        <Switch
          checked={endpoint.enabled}
          disabled={updating}
          onChange={(event) => onToggle(event.target.checked)}
          label={endpoint.enabled ? 'Recibiendo avisos' : 'En pausa'}
          aria-label={`${endpoint.enabled ? 'Pausar' : 'Activar'} avisos para ${endpoint.url}`}
        />
      </div>

      {status.hint && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{status.hint}</span>
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onTest} disabled={testing}>
          <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {testing ? 'Enviando…' : 'Enviar prueba'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onHistory}>
          <History className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Historial
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Editar
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onRotate}>
          <KeyRound className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Rotar secreto
        </Button>
        <Button type="button" size="sm" variant="destructive" onClick={onDelete}>
          <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Eliminar
        </Button>
      </div>
    </article>
  )
}
